import { apiClient } from './client';
import type {
  BranchModule,
  EffectiveAccess,
  ManualAccess,
  ManualAccessWrite,
  RoleAccess,
  RoleAccessWrite,
} from '@/types/oscar';

/**
 * Typed wrappers over `/api/branch-relationships/*` (migration 128; Data-Model
 * §17). Reads are admin + helpdesk; every write is admin-only (enforced
 * server-side). Identifiers travel in the body because module keys contain a
 * dot (`finance.ap`).
 */

const base = '/api/branch-relationships';

/** GET modules — the live oscar.Module catalogue. */
export function listBranchModules(signal?: AbortSignal): Promise<BranchModule[]> {
  return apiClient.get<BranchModule[]>(`${base}/modules`, signal);
}

/** GET role-access — role → module mappings (withdrawn too, so they can be revived). */
export function listRoleAccess(includeWithdrawn = true, signal?: AbortSignal): Promise<RoleAccess[]> {
  return apiClient.get<RoleAccess[]>(`${base}/role-access${includeWithdrawn ? '?includeWithdrawn=true' : ''}`, signal);
}

export function addRoleAccess(body: RoleAccessWrite): Promise<RoleAccess> {
  return apiClient.post<RoleAccess>(`${base}/role-access`, body);
}

/** PATCH role-access — switch a live mapping between the geographic rule and Company. */
export function updateRoleAccess(body: RoleAccessWrite): Promise<RoleAccess> {
  return apiClient.patch<RoleAccess>(`${base}/role-access`, body);
}

export function withdrawRoleAccess(body: RoleAccessWrite): Promise<RoleAccess> {
  return apiClient.post<RoleAccess>(`${base}/role-access/withdraw`, body);
}

export function reviveRoleAccess(body: RoleAccessWrite): Promise<RoleAccess> {
  return apiClient.post<RoleAccess>(`${base}/role-access/revive`, body);
}

/** GET manual — manual rows (withdrawn too, shown greyed). */
export function listManualAccess(includeWithdrawn = true, signal?: AbortSignal): Promise<ManualAccess[]> {
  return apiClient.get<ManualAccess[]>(`${base}/manual${includeWithdrawn ? '?includeWithdrawn=true' : ''}`, signal);
}

export function addManualAccess(body: ManualAccessWrite): Promise<ManualAccess> {
  return apiClient.post<ManualAccess>(`${base}/manual`, body);
}

export function withdrawManualAccess(id: number): Promise<ManualAccess> {
  return apiClient.post<ManualAccess>(`${base}/manual/${id}/withdraw`);
}

/** GET effective — oscar.vPersonBranchScope for one person ("who can see what"). */
export function getEffectiveAccess(personId: number, signal?: AbortSignal): Promise<EffectiveAccess> {
  return apiClient.get<EffectiveAccess>(`${base}/effective?personId=${personId}`, signal);
}
